import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "./axiosBaseQuery";
import FintreeConfig from "../app/config";
import ApiUser from "@/model/interface/api/ApiUser";
import { ApiApplication } from "@/model/interface/api/ApiApplication";
import ApplicantDocumentType from "@/model/interface/ApplicantDocumentType";
import ApiEnquiry from "@/model/interface/api/ApiEnquiry";

interface PagedResponse<T> {
  items: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${FintreeConfig.baseAPIEndpoint}`,
  }),
  tagTypes: ["AdminUser", "AdminApplication", "AdminEnquiry", "AdminDocumentType"],
  endpoints: (builder) => ({
    getAdminUsers: builder.query<PagedResponse<ApiUser>, { page?: number; size?: number; search?: string }>({
      query: ({ page = 0, size = 20, search = "" }) => ({
        url: `/v1/admin/users?page=${page}&size=${size}&search=${encodeURIComponent(search)}`,
        method: "get",
      }),
      providesTags: ["AdminUser"],
    }),
    getAdminUser: builder.query<ApiUser, string>({
      query: (id) => ({ url: `/v1/admin/users/${id}`, method: "get" }),
      providesTags: (result, error, id) => [{ type: "AdminUser", id }],
    }),
    putAdminUserRoles: builder.mutation<ApiUser, { id: string; roles: string[] }>({
      query: ({ id, roles }) => ({
        url: `/v1/admin/users/${id}/roles`,
        method: "put",
        data: { roles },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["AdminUser"]),
    }),
    getAdminApplications: builder.query<PagedResponse<ApiApplication>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 20 }) => ({
        url: `/v1/admin/applications?page=${page}&size=${size}`,
        method: "get",
      }),
      providesTags: ["AdminApplication"],
    }),
    getAdminApplication: builder.query<ApiApplication, string>({
      query: (id) => ({ url: `/v1/admin/applications/${id}`, method: "get" }),
      providesTags: (result, error, id) => [{ type: "AdminApplication", id }],
    }),
    getAdminEnquiries: builder.query<PagedResponse<ApiEnquiry>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 20 }) => ({
        url: `/v1/admin/enquiries?page=${page}&size=${size}`,
        method: "get",
      }),
      providesTags: ["AdminEnquiry"],
    }),
    getAdminEnquiry: builder.query<ApiEnquiry, string>({
      query: (id) => ({ url: `/v1/admin/enquiries/${id}`, method: "get" }),
      providesTags: (result, error, id) => [{ type: "AdminEnquiry", id }],
    }),
    getAdminDocumentTypes: builder.query<ApplicantDocumentType[], void>({
      query: () => ({
        url: `/v1/admin/document-types`,
        method: "get",
      }),
      transformResponse: (response: any[]) =>
        response.map((dt: any) => ({
          ...dt,
          fields: typeof dt.fields === "string" ? JSON.parse(dt.fields) : dt.fields,
        })),
      providesTags: ["AdminDocumentType"],
    }),
    postAdminDocumentType: builder.mutation<ApplicantDocumentType, Partial<ApplicantDocumentType>>({
      query: (data) => ({
        url: `/v1/admin/document-types`,
        method: "post",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["AdminDocumentType"]),
    }),
    putAdminDocumentType: builder.mutation<ApplicantDocumentType, Partial<ApplicantDocumentType> & { id: string }>({
      query: ({ id, ...data }) => ({
        url: `/v1/admin/document-types/${id}`,
        method: "put",
        data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["AdminDocumentType"]),
    }),
    deleteAdminDocumentType: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/admin/document-types/${id}`,
        method: "delete",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["AdminDocumentType"]),
    }),
  }),
});

export const {
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  usePutAdminUserRolesMutation,
  useGetAdminApplicationsQuery,
  useGetAdminApplicationQuery,
  useGetAdminEnquiriesQuery,
  useGetAdminEnquiryQuery,
  useGetAdminDocumentTypesQuery,
  usePostAdminDocumentTypeMutation,
  usePutAdminDocumentTypeMutation,
  useDeleteAdminDocumentTypeMutation,
} = adminApi;
